import { formatQuantityDisplay } from '@/composables/useQuantity'
import { buildReceiptTaxDisplayLines } from '@/utils/receiptTaxDisplay'
import {
  getSaleCashRoundingAdjustment,
  shouldShowCashRoundingOnReceipt
} from '@/utils/receiptCashRoundingDisplay'
import { formatCashRoundingAdjustment } from '@/utils/cashPennyRounding'
import { tabEntryReceiptLineItemsFromContext } from '@/utils/tabEntryReceiptItems'
import { invoicePaymentReceiptLineItemsFromContext } from '@/utils/invoicePaymentReceiptItems'
import { appendThermalCardPaymentDetails } from '@/utils/thermalReceiptCardPayment'
import { formatSalePaymentRowLabelEn } from '@/utils/saleDisplay'
import {
  appendLightweightTabPaymentReceiptBody,
  appendStructuredTabPaymentReceiptBody,
  appendTabPaymentReceiptHeader,
  isStructuredTabPaymentReceipt
} from '@/utils/thermalReceiptVolcoraTabPayment'
import { resolveSaleCustomerReceiptLabel } from '@/utils/saleCustomerDisplay'

const COLS_80 = 48
const COLS_58 = 32

export function getThermalColumns (paperWidthMm) {
  return Number(paperWidthMm) === 58 ? COLS_58 : COLS_80
}

function fmtMoney (n) {
  const x = Number(n) || 0
  return `$${x.toFixed(2)}`
}

function wrapToWidth (str, width) {
  const s = String(str || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const words = s.split(/\s+/)
  const lines = []
  let cur = ''
  for (const w of words) {
    if (!w) continue
    const next = cur ? `${cur} ${w}` : w
    if (next.length <= width) {
      cur = next
    } else {
      if (cur) lines.push(cur)
      if (w.length <= width) {
        cur = w
      } else {
        for (let i = 0; i < w.length; i += width) {
          lines.push(w.slice(i, i + width))
        }
        cur = ''
      }
    }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : ['']
}

function lineItemLineTotalRaw (row) {
  if (row.redeeming_for_points) {
    const totalCost = parseInt(row.total_redemption_cost, 10) || 0
    return `${totalCost} pts`
  }
  const price = parseFloat(row.price) || 0
  const quantity = Number(row.quantity) || 0
  let discount
  if (row.discount_type === 'percentage') {
    discount = price * quantity * (parseFloat(row.discount_amount) / 100)
  } else {
    discount = parseFloat(row.discount_amount) || 0
  }
  return fmtMoney(price * quantity - discount)
}

function appendSaleCustomerReceiptLine (addLines, sale, L) {
  const label = L.customer || 'Customer'
  const walkIn = L.walkInCustomer || 'Walk-In'
  addLines(`${label}: ${resolveSaleCustomerReceiptLabel(sale, walkIn)}`)
}

function padBetween (left, right, width) {
  const L = String(left)
  const R = String(right)
  const space = width - L.length - R.length
  if (space <= 0) {
    return `${L} ${R}`
  }
  return `${L}${' '.repeat(space)}${R}`
}

function receiptItemRows (sale, L = {}) {
  const items = Array.isArray(sale.items) ? [...sale.items] : []
  const registrant = sale.event_registrant
  if (registrant) {
    const eventTitle = registrant.event?.title || 'Event'
    const packageName = registrant.ticket_package?.name
    const label = `Event: ${eventTitle}${packageName ? ` (${packageName})` : ''}`
    const quantity = Number(registrant.quantity || 1)
    const amountPaid = Number(registrant.amount_paid || 0)
    const unitPrice = quantity > 0 ? amountPaid / quantity : amountPaid
    items.push({
      id: `event-${registrant.id || 'sale'}`,
      name: label,
      quantity,
      price: unitPrice,
      redeeming_for_points: false,
      discount_amount: 0,
      product: { name: label, allow_fractional: false }
    })
  }
  const ctx = sale.tab_payment_context
  if (ctx && !isStructuredTabPaymentReceipt(sale) && ctx.has_full_snapshot !== false) {
    const tabEntryItems = tabEntryReceiptLineItemsFromContext(ctx)
    if (tabEntryItems.length) {
      for (const entryItem of tabEntryItems) {
        items.push({
          ...entryItem,
          redeeming_for_points: false,
          discount_amount: 0,
          product: { name: entryItem.name, allow_fractional: false }
        })
      }
    } else {
      const amountMinor = Number(ctx.payment_amount_minor || 0)
      const amount = amountMinor / 100
      const tabId = ctx.tab_id ?? ''
      const lineTemplate = L.tabPaymentLine || 'Tab payment — Tab #{id}'
      const label = lineTemplate.replace('{id}', String(tabId))
      items.push({
        id: `tab-payment-${ctx.tab_payment_id || tabId || 'sale'}`,
        name: label,
        quantity: 1,
        price: amount,
        redeeming_for_points: false,
        discount_amount: 0,
        product: { name: label, allow_fractional: false }
      })
    }
  }
  const invoiceItems = invoicePaymentReceiptLineItemsFromContext(sale.invoice_payment_context)
  for (const invoiceItem of invoiceItems) {
    items.push({
      ...invoiceItem,
      redeeming_for_points: false,
      discount_amount: 0,
      product: { name: invoiceItem.name, allow_fractional: false }
    })
  }
  return items
}

function appendPaymentsSection (ops, sale, L, cols, op, addLines) {
  ops.push(op({ type: 'text', text: '\r\n' }))
  ops.push(op({ type: 'align', value: 0 }))
  ops.push(op({ type: 'text', text: `${L.payments || 'Payments'}\r\n`, thick: 1 }))

  const structuredTabPayment = isStructuredTabPaymentReceipt(sale)
  const hiddenStatuses = new Set(['failed', 'declined', 'pending', 'processing'])
  const payments = (Array.isArray(sale.payments) ? sale.payments : [])
    .filter((p) => !hiddenStatuses.has(String(p?.status ?? '').toLowerCase()))
  if (payments.length) {
    for (const p of payments) {
      const label = formatSalePaymentRowLabelEn(p)
      ops.push(
        op({
          type: 'text',
          text: `${padBetween(label, fmtMoney(p.amount || 0), cols)}\r\n`
        })
      )
      if (!structuredTabPayment) {
        const tip = Number(p.tip_amount || 0)
        const fee = Number(p.fee_amount || 0)
        if (tip > 0) {
          ops.push(
            op({
              type: 'text',
              text: `${padBetween(L.tip || 'Tip', fmtMoney(tip), cols)}\r\n`
            })
          )
        }
        if (fee > 0) {
          ops.push(
            op({
              type: 'text',
              text: `${padBetween(L.fee || 'Fee', fmtMoney(fee), cols)}\r\n`
            })
          )
        }
      }
      if (p.payment_method === 'cash') {
        const recv = Number(p.amount_received || 0)
        const chg = Number(p.change_given || 0)
        if (recv > 0) {
          ops.push(
            op({
              type: 'text',
              text: `${padBetween(L.received || 'Received', fmtMoney(recv), cols)}\r\n`
            })
          )
        }
        if (chg > 0) {
          ops.push(
            op({
              type: 'text',
              text: `${padBetween(L.change || 'Change', fmtMoney(chg), cols)}\r\n`
            })
          )
        }
      }
      appendThermalCardPaymentDetails(addLines, p, L)
    }
  }
}

function appendStandardSaleBody (ops, sale, L, cols, op, addLines) {
  const items = receiptItemRows(sale, L)
  for (const row of items) {
    const name =
      row.product?.name || row.name || L.unknownProduct || 'Item'
    const qty = formatQuantityDisplay(row.quantity, {
      allowFractional: row.product?.allow_fractional ?? null
    })
    const priceCell = row.redeeming_for_points
      ? `${parseInt(row.redemption_cost, 10) || 0} pts`
      : fmtMoney(row.price)
    const totalCell = lineItemLineTotalRaw(row)
    addLines(`${qty} x ${priceCell}`)
    for (const nl of wrapToWidth(name, cols)) {
      addLines(nl)
    }
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.lineTotal || 'Line', totalCell, cols)}\r\n`,
        thick: 1
      })
    )
  }

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))

  const subtotal = Number(sale.base_subtotal || sale.subtotal || 0)
  ops.push(
    op({
      type: 'text',
      text: `${padBetween(L.subtotal || 'Subtotal', fmtMoney(subtotal), cols)}\r\n`
    })
  )

  if (Number(sale.discount_amount || 0) > 0) {
    const d = sale.discount_type === 'percentage'
      ? `${formatQuantityDisplay(sale.discount_amount, { allowFractional: true })}%`
      : fmtMoney(sale.discount_amount)
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.discount || 'Discount', d, cols)}\r\n`
      })
    )
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.afterDiscount || 'After discount', fmtMoney(sale.subtotal || 0), cols)}\r\n`
      })
    )
  }

  const taxLines = buildReceiptTaxDisplayLines(sale, { genericTaxLabel: L.tax || 'Tax' })
  for (const line of taxLines) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(line.label, fmtMoney(line.amount || 0), cols)}\r\n`
      })
    )
  }

  if (Number(sale.tip_total || 0) > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.tip || 'Tip', fmtMoney(sale.tip_total), cols)}\r\n`
      })
    )
  }

  if (Number(sale.convenience_fee_total || 0) > 0) {
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.fee || 'Fee', fmtMoney(sale.convenience_fee_total), cols)}\r\n`
      })
    )
  }

  if (shouldShowCashRoundingOnReceipt(sale)) {
    const adjustment = getSaleCashRoundingAdjustment(sale)
    ops.push(
      op({
        type: 'text',
        text: `${padBetween(L.pennyRounding || 'Penny rounding', formatCashRoundingAdjustment(adjustment), cols)}\r\n`
      })
    )
  }

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))
  const totalLabel = shouldShowCashRoundingOnReceipt(sale)
    ? (L.cashTotal || 'TOTAL (CASH)')
    : (L.total || 'TOTAL')
  ops.push(
    op({
      type: 'text',
      text: `${padBetween(totalLabel, fmtMoney(sale.total || 0), cols)}\r\n`,
      doubleWidth: true,
      thick: 1
    })
  )
}

/**
 * Build Volcora print operations (see src-electron/volcora/volcoraPosSdk.js applyOp).
 * @param {object} sale
 * @param {object | null} businessInfo
 * @param {object} opts
 * @param {number} opts.paperWidthMm
 * @param {Record<string, string>} opts.labels - translated short labels for printed receipt
 * @param {string} [opts.logoBmpBase64] - 1bpp BMP as base64 from `fetchBusinessLogoAsBmpBase64` (IPC expands to temp file)
 */
export function buildVolcoraThermalReceiptOps (sale, businessInfo, opts) {
  const cols = getThermalColumns(opts?.paperWidthMm ?? 80)
  const L = opts?.labels || {}
  const op = (o) => o
  const ctx = sale.tab_payment_context
  const structuredTabPayment = isStructuredTabPaymentReceipt(sale)
  const lightweightTabPayment = ctx && ctx.has_full_snapshot === false

  const ops = []
  const addLines = (text, fontProps = {}) => {
    const lines = wrapToWidth(text, cols)
    for (const ln of lines) {
      ops.push(op({ type: 'text', text: `${ln}\r\n`, ...fontProps }))
    }
  }

  ops.push(op({ type: 'align', value: 1 }))
  if (opts?.logoBmpBase64) {
    ops.push(op({ type: 'bmpDirect', dataBase64: opts.logoBmpBase64 }))
    ops.push(op({ type: 'text', text: '\r\n' }))
  }
  if (businessInfo?.name || businessInfo?.business_name) {
    addLines(businessInfo.name || businessInfo.business_name, { font: 0, doubleWidth: true, doubleHeight: true, thick: 1 })
  }
  ops.push(op({ type: 'align', value: 0 }))
  if (businessInfo?.address) addLines(businessInfo.address)
  if (businessInfo?.phone) {
    addLines(`${L.phone || 'Phone'}: ${businessInfo.phone}`)
  }
  if (businessInfo?.tax_id) {
    addLines(`${L.taxId || 'Tax ID'}: ${businessInfo.tax_id}`)
  }

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))

  if (structuredTabPayment || lightweightTabPayment) {
    appendTabPaymentReceiptHeader(ops, sale, ctx, L, cols, op, addLines, businessInfo)
    if (structuredTabPayment) {
      appendStructuredTabPaymentReceiptBody(ops, ctx, L, cols, op, addLines, sale)
    } else {
      appendLightweightTabPaymentReceiptBody(ops, ctx, L, cols, op, addLines, sale)
    }
  } else {
    const created = sale.created_at ? new Date(sale.created_at) : new Date()
    addLines(`${L.date || 'Date'}: ${created.toLocaleString()}`)
    addLines(`${L.sale || 'Sale'} #${sale.id}`)
    if (sale.sale_source === 'terminal_feed') {
      addLines(`${L.saleSource || 'Source'}: ${L.saleSourceTerminalFeed || 'Terminal (FEED)'}`)
    }
    if (sale.user?.name && businessInfo?.show_cashier_on_receipt !== false) {
      addLines(`${L.cashier || 'Cashier'}: ${sale.user.name}`)
    }
    appendSaleCustomerReceiptLine(addLines, sale, L)
    ops.push(op({ type: 'text', text: '\r\n' }))
    appendStandardSaleBody(ops, sale, L, cols, op, addLines)
  }

  appendPaymentsSection(ops, sale, L, cols, op, addLines)

  ops.push(op({ type: 'text', text: '\r\n' }))
  ops.push(op({ type: 'align', value: 1 }))
  const thank = L.thankYou || 'Thank you!'
  addLines(thank)

  const saleUrl = typeof opts?.publicSaleUrl === 'string' ? opts.publicSaleUrl : ''
  if (saleUrl) {
    ops.push(op({ type: 'text', text: '\r\n' }))
    ops.push(op({ type: 'qr', data: saleUrl, version: 4, errorLevel: 77, magnify: 3 }))
  }

  ops.push(op({ type: 'text', text: '\r\n\r\n' }))
  return ops
}

export { isStructuredTabPaymentReceipt } from '@/utils/thermalReceiptVolcoraTabPayment'

export function buildVolcoraThermalTestOps (businessInfo, opts) {
  const L = opts?.labels || {}
  const cols = getThermalColumns(opts?.paperWidthMm ?? 80)
  const lines = [
    L.testBanner || 'Blu POS - printer test',
    '---',
    businessInfo?.name || 'Store',
    new Date().toLocaleString(),
    `${L.portLabel || 'Port OK'}`,
    '-'.repeat(Math.min(42, cols)),
    L.testFooter || 'End of test'
  ]
  const ops = []
  ops.push({ type: 'align', value: 1 })
  if (opts?.logoBmpBase64) {
    ops.push({ type: 'bmpDirect', dataBase64: opts.logoBmpBase64 })
    ops.push({ type: 'text', text: '\r\n' })
  }
  for (const row of lines) {
    ops.push({ type: 'text', text: `${row}\r\n` })
  }
  ops.push({ type: 'text', text: '\r\n\r\n' })
  return ops
}

/**
 * @returns {boolean}
 */
export function saleHasCashPayment (sale) {
  if (!sale) return false
  const payments = sale.payments
  if (Array.isArray(payments) && payments.some((p) => p?.payment_method === 'cash')) {
    return true
  }
  return sale.payment_method === 'cash'
}
